import { Outlet, useSearchParams } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import "../../../Styles/Navbar.css";
import { useLocation } from "react-router-dom";
import CategoryNav from "../CategoryNav/CategoryNav";
import MobileNav from "../CategoryNav/MobileNav";
import { checkAuth } from "../../../Services/Https/auth";
import Footer from "../Footer";
import { useTitle } from "../../TitleContext/TitleContext";
import {
  AppBar,
  IconButton,
  Toolbar,
  Box,
  InputBase,
  Paper,
  Tooltip,
  Snackbar,
  Slide,
  Backdrop,
  SlideProps,
  Button,
  Typography,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { FiSearch, FiShoppingBag, FiMapPin, FiMenu } from "../../../icons";
import { useSearchContext } from "../../SearchContext/SearchContext";
import color from "../../../Constants/colors";
import { CSSTransition } from "react-transition-group";
import useScreenSize from "../../../Hooks/Misc/useScreenSize";
import CartPopup from "../CartPopup";
import CardIcons from "../CardIcons";
import colors from "../../../Constants/colors";
import Informations from "../Informations/Informations";

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  transition: theme.transitions.create(["margin", "padding"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create(["margin", "padding"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    margin: 0,
    padding: 0,
  }),
  overflowX: "hidden",
  maxWidth: "100%",
}));

// Spacer that pushes content below the fixed AppBar. Heights mirror the
// .navbar-container heights in Navbar.css across breakpoints.
const DrawerHeader = styled("div")(() => ({
  height: 84,
  "@media screen and (max-width: 1024px)": { height: 68 },
  "@media screen and (max-width: 480px)": { height: 60 },
}));

const Navbar = () => {
  const navigate = useNavigate();
  const phone = useScreenSize();
  const mobileNav = useScreenSize("1024");
  const [openSearch, setOpenSearch] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [auth, setAuth] = useState(false);
  const location = useLocation();
  const { search, setSearch } = useSearchContext();
  const [, setSearchParams] = useSearchParams();
  // state za cookies Snackbar
  const [cookiesSnackbarOpen, setCookiesSnackbarOpen] = useState(false);
  const { setTitle, setSubtitle } = useTitle();

  // tranzicija za dolazak sa donje strane ekrana
  const SlideTransition = (props: JSX.IntrinsicAttributes & SlideProps) => {
    return <Slide {...props} direction="up" />;
  };

  useEffect(() => {
    if (!localStorage.getItem("cookiesSnackbarShown")) {
      setCookiesSnackbarOpen(true);
    }
  }, []);
  const handleCookiesSnackbarClose = () => {
    localStorage.setItem("cookiesSnackbarShown", "true");
    setCookiesSnackbarOpen(false);
  };

  const handleOpenSearch = () => {
    if (!openSearch) {
      setOpenCart(false);
      setOpenMenu(false);
    }
    setOpenSearch(!openSearch);
  };

  const handleOpenCart = () => {
    if (!openCart) {
      setOpenSearch(false);
      setOpenMenu(false);
    }
    setOpenCart(!openCart);
  };

  const handleOpenMenu = () => {
    if (!openMenu) {
      setOpenSearch(false);
      setOpenCart(false);
    }
    setOpenMenu(!openMenu);
  };

  useEffect(() => {
    const fetchAuth = async () => {
      try {
        const response = await checkAuth();
        setAuth(response.status === 200);
      } catch (error: unknown) {
        setAuth(false);
      }
    };
    fetchAuth();
  }, []);

  const handleClickContact = () => {
    const element = document.getElementById("footer");
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const offset = 0.1;
      window.scrollTo({
        top: absoluteElementTop + offset,
        behavior: "smooth",
      });
    }
  };

  const renderSearchBar = () => {
    const commitSearch = () => {
      setSearchParams({ search: search });
      setTitle(`Rezultati pretrage: ${search}`);
      setSubtitle(" ");
      sessionStorage.setItem("page", "0");
      window.dispatchEvent(new Event("pageReset"));
      if (location.pathname !== "/products") {
        navigate(`/products?search=${search}`, {
          state: {
            material: null,
            briliant: null,
            category_id: null,
            subcategory_id: null,
            order: "desc",
            orderBy: "popular",
          },
        });
      }
    };
    return (
      <CSSTransition
        in={openSearch}
        timeout={300}
        classNames="fade"
        unmountOnExit
      >
        <div className="search-bar-container">
          <Paper component="form" className="search-bar" elevation={0}>
            <FiSearch className="search-bar-icon" aria-hidden />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Pretraži proizvode"
              inputProps={{ "aria-label": "search" }}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitSearch();
                  e.preventDefault();
                }
              }}
            />
            <IconButton
              type="button"
              sx={{ p: "8px" }}
              aria-label="search"
              onClick={commitSearch}
            >
              <FiSearch color={color.dark} />
            </IconButton>
          </Paper>
        </div>
      </CSSTransition>
    );
  };

  const renderActionButton = (
    icon: JSX.Element,
    label: string,
    onClick: () => void
  ) => (
    <button type="button" className="nav-action" onClick={onClick}>
      {icon}
      {!phone && <span className="nav-action-label">{label}</span>}
    </button>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: colors.white,
          color: colors.text,
          borderBottom: `1px solid ${colors.line}`,
        }}
      >
        <Toolbar disableGutters className="navbar-toolbar">
          <div className="navbar-container">
            <div className="navbar-left">
              {mobileNav && (
                <button
                  type="button"
                  className="nav-action nav-menu-btn"
                  aria-label="Otvori meni"
                  onClick={handleOpenMenu}
                >
                  <FiMenu size={24} />
                </button>
              )}
              <div
                className="navbar-brand"
                onClick={() => navigate("")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") navigate("");
                }}
              >
                <span className="navbar-brand-name">DORO</span>
                <span className="navbar-brand-sub">JEWELRY SHOP</span>
              </div>
            </div>

            {!mobileNav && (
              <div className="navbar-center">
                <CategoryNav />
              </div>
            )}

            <div className="navbar-right">
              {renderActionButton(
                <FiSearch size={22} />,
                "Pretraga",
                handleOpenSearch
              )}
              {renderActionButton(
                <FiShoppingBag size={22} />,
                "Korpa",
                handleOpenCart
              )}
              {!mobileNav &&
                renderActionButton(
                  <FiMapPin size={22} />,
                  "Kontakt",
                  handleClickContact
                )}
            </div>
          </div>
        </Toolbar>
        {renderSearchBar()}
        {openCart ? <CartPopup handleOpenCart={handleOpenCart} /> : null}
      </AppBar>

      {mobileNav && (
        <MobileNav open={openMenu} onClose={() => setOpenMenu(false)} />
      )}

      <Main>
        <DrawerHeader />
        <Outlet />
        <Footer />
        <CardIcons />
        <Informations />
        {auth ? (
          <Tooltip title="Idi na Admin Panel">
            <IconButton
              sx={{
                position: "fixed",
                bottom: "3vh",
                right: "3vh",
                zIndex: 1000,
              }}
              onClick={() => {
                navigate("/admin");
              }}
            >
              <AdminPanelSettingsIcon
                sx={{ color: colors.accent }}
                className="admin-float-icon"
              />
            </IconButton>
          </Tooltip>
        ) : null}
        <Backdrop
          open={cookiesSnackbarOpen}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <Snackbar
            open={cookiesSnackbarOpen}
            TransitionComponent={SlideTransition}
            message={
              <Typography align="center">
                U ime Jewelry Shop Doro obavezujemo se da ćemo čuvati privatnost
                svih naših kupaca. Prikupljamo samo neophodne, osnovne podatke o
                kupcima/ korisnicima i podatke neophodne za poslovanje i
                informisanje korisnika u skladu sa dobrim poslovnim običajima i
                u cilju pružanja kvalitetne usluge. Svi podaci o
                korisnicima/kupcima se strogo čuvaju i dostupni su samo
                zaposlenima kojima su ti podaci nužni za obavljanje posla. Svi
                zaposleni Jewelery Shop Doro (i poslovni partneri) odgovorni su
                za poštovanje načela zaštite privatnosti.
              </Typography>
            }
            action={
              <Button
                variant="contained"
                sx={{ color: colors.white, backgroundColor: colors.accent }}
                size="small"
                onClick={handleCookiesSnackbarClose}
              >
                Prihvatam
              </Button>
            }
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          />
        </Backdrop>
      </Main>
    </Box>
  );
};

export default Navbar;
